import { Capacitor } from '@capacitor/core';
import { useMutation } from '@tanstack/vue-query';
import { computed, ref } from 'vue';
import type {
  RentalKeyTagPrepareDto,
  RentalKeyTagRegisteredDto,
  RentalKeyTagSdmKeyDto,
} from '@carvia/ros-client-types';
import NfcSunPlugin from '@/capacitor-plugins/NfcSunPlugin';
import { useNfcSunOverlay } from '@/capacitor-plugins/nfc-sun-overlay';
import { httpClient } from '@/api/http-client';

export const isRentalKeyTagProgrammingAvailable = (): boolean =>
  import.meta.env.VITE_RENTAL_KEY_TAG_ENABLED !== 'false' &&
  (import.meta.env.VITE_RENTAL_KEY_TAG_MOCK === 'true' ||
    (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('NfcSun')));

const isMockProgrammingEnabled = import.meta.env.VITE_RENTAL_KEY_TAG_MOCK === 'true';

export const useRentalKeyTagProgramming = (bookingId: () => string) => {
  const nfcOverlay = useNfcSunOverlay();
  const prepareMutation = useMutation({
    mutationFn: async () => {
      const { data } = await httpClient.post<RentalKeyTagPrepareDto>('/rental-key-tag/prepare', {
        bookingId: bookingId(),
      });
      return data;
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (payload: { tagUid: string }) => {
      const { data } = await httpClient.post<RentalKeyTagRegisteredDto>(
        '/rental-key-tag/register',
        {
          bookingId: bookingId(),
          tagUid: payload.tagUid,
        },
      );
      return data;
    },
  });

  const mockRegisterMutation = useMutation({
    mutationFn: async () => {
      const { data } = await httpClient.post<{
        registration: RentalKeyTagRegisteredDto;
        tagUid: string;
        sdmKey: string;
      }>('/rental-key-tag/mock/register', {
        bookingId: bookingId(),
      });
      return data;
    },
  });

  const isWritingTag = ref(false);

  const programTag = async (): Promise<void> => {
    if (isWritingTag.value) {
      return;
    }
    isWritingTag.value = true;
    try {
      if (isMockProgrammingEnabled) {
        await mockRegisterMutation.mutateAsync();
        return;
      }
      const prepared = await prepareMutation.mutateAsync();
      const listener = await NfcSunPlugin.addListener('needSdmKey', async (event) => {
        try {
          const { data } = await httpClient.post<RentalKeyTagSdmKeyDto>('/rental-key-tag/sdm-key', {
            bookingId: bookingId(),
            tagUid: event.uid,
          });
          await NfcSunPlugin.continueWithSdmKey({ sdmKey: data.sdmKey });
        } catch (lookupError) {
          const message =
            lookupError instanceof Error ? lookupError.message : 'Failed to load SUN key';
          await NfcSunPlugin.abortSun({ message });
        }
      });
      try {
        const result = await NfcSunPlugin.configureSun({
          baseUrl: prepared.tapBaseUrl,
          currentKey: prepared.currentKey,
          provisionKey1: true,
          alertMessage: nfcOverlay.alertMessage(),
          successMessage: nfcOverlay.successMessage(),
        });
        await registerMutation.mutateAsync({ tagUid: result.uid });
      } finally {
        await listener.remove();
      }
    } finally {
      isWritingTag.value = false;
    }
  };

  return {
    isMockProgrammingEnabled,
    programTag,
    isProgramming: computed(
      () =>
        isWritingTag.value ||
        prepareMutation.isPending.value ||
        registerMutation.isPending.value ||
        mockRegisterMutation.isPending.value,
    ),
  };
};
